import {
  Badge as MantineBadge,
  BadgeProps as MantineBadgeProps,
  MantineTheme,
} from '@mantine/core';

import classes from './Badge.module.css';

const BadgeTypes = ['primary', 'success', 'error', 'info', 'warning', 'neutral', 'white'] as const;

interface BadgeProps extends MantineBadgeProps {
  type?: (typeof BadgeTypes)[number];
}

export const BadgeTypeStyles = (theme: MantineTheme) => ({
  primary: {
    filled: {
      backgroundColor: theme?.colors.cobalt[6],
      color: theme?.colors.white[0],
    },
    light: {
      backgroundColor: theme?.colors.cobalt[0],
      color: theme?.colors.cobalt[6],
    },
    outline: {},
  },
  success: {
    filled: {
      backgroundColor: theme?.colors.mint[4],
      color: theme?.colors.gray[8],
    },
    light: {
      backgroundColor: theme?.colors.mint[0],
      color: theme?.colors.mint[9],
    },
    outline: {
      color: theme?.colors.mint[8],
      borderColor: theme?.colors.mint[8],
    },
  },
  error: {
    filled: {
      backgroundColor: theme?.colors.cerise[6],
      color: theme?.colors.white[0],
    },
    light: {
      backgroundColor: theme?.colors.cerise[1],
      color: theme?.colors.cerise[7],
    },
    outline: {
      color: theme?.colors.cerise[6],
      borderColor: theme?.colors.cerise[6],
    },
  },
  info: {
    filled: {
      backgroundColor: theme?.colors.lightBlue[5],
      color: theme?.colors.gray[8],
    },
    light: {
      backgroundColor: theme?.colors.lightBlue[1],
      color: theme?.colors.gray[8],
    },
    outline: {
      color: theme?.colors.gray[6],
      borderColor: theme?.colors.lightBlue[5],
    },
  },
  warning: {
    filled: {
      backgroundColor: theme?.colors.yellow[4],
      color: theme?.colors.gray[8],
    },
    light: {
      backgroundColor: theme?.colors.yellow[1],
      color: theme?.colors.gray[8],
    },
    outline: {
      borderColor: theme?.colors.yellow[4],
      color: theme?.colors.gray[8],
    },
  },
  neutral: {
    filled: {
      backgroundColor: theme?.colors.gray[2],
      color: theme?.colors.gray[8],
    },
    light: {
      backgroundColor: theme?.colors.gray[1],
      color: theme?.colors.gray[8],
    },
    outline: {
      borderColor: theme?.colors.gray[2],
      color: theme?.colors.gray[6],
    },
  },
  white: {
    filled: {
      backgroundColor: theme?.colors.white[0],
      color: theme?.colors.gray[8],
    },
    outline: {
      borderColor: theme?.colors.white[0],
      color: theme?.colors.white[0],
    },
    light: {},
  },
});

const BadgeDefaults = MantineBadge.extend({
  defaultProps: {
    variant: 'light',
  },
  classNames: {
    root: classes.root,
    label: classes.label,
  },
  styles(theme, props) {
    const badgeType = props?.mod?.['badge-type'] ?? 'primary';
    const typeStyles = BadgeTypeStyles(theme)[badgeType][props.variant];

    return {
      root: {
        '--badge-custom-background-color': typeStyles.backgroundColor ?? undefined,
        '--badge-custom-color': typeStyles.color ?? undefined,

        // Originally had tried to pass this as a css variable, but that method forced us to set a border-color in css.
        // This method allows us the default mantine default border colors to be used when the typeStyles.borderColor
        // has no value.
        ...(typeStyles.borderColor
          ? {
              borderColor: typeStyles.borderColor,
            }
          : {}),
      },
    };
  },
});

const Badge = ({ type, ...props }: BadgeProps) => {
  return (
    <MantineBadge
      mod={{
        ['badge-type']: type,
      }}
      {...props}
    />
  );
};

export { Badge, BadgeDefaults, BadgeTypes };
export type { BadgeProps };
